// Canonical-trust tiers — at-a-glance signal of how well we know a
// canonical ingredient. Three buckets:
//
//   GREY  ("just there")
//     - No canonical id at all (pure free-text row)
//     - Synthetic canonical still PENDING admin review
//     - A bundled / approved canonical with no nutrition + no
//       enrichment beyond its name
//     - Dangling canonical_id that doesn't resolve to a registry row
//     The user has named something but the system has no real data
//     on it. Cooking with these works; nutrition rollup degrades.
//
//   GREEN ("global memory")
//     - Bundled INGREDIENTS row OR admin-approved ingredient_info row
//     - Has a category, may have AI-extracted nutrition (Haiku /
//       generic 100g defaults), but no verified UPC trust path
//     The canonical is "real" enough that everyone benefits — but the
//     numbers may be approximations.
//
//   BLUE  ("verified")
//     - Nutrition is present AND came from a verified source: USDA
//       FDC, scan-time nutrition-label OCR with a UPC tie-back, or
//       admin direct-curated
//     The gold tier. Picking this canonical for a recipe means the
//     calorie / macro rollup is trustworthy.
//
// The tier function is PURE — it accepts the resolver helpers as a
// parameter object so it can be called from any layer (React render,
// non-React utility, scripts) without a circular import on the
// useIngredientInfo hook.

// Sources we treat as "verified enough" for the BLUE tier. Haiku-
// extracted nutrition does NOT count — it's an educated guess. USDA
// FDC IDs, brand_nutrition rows tied to a UPC, admin-curated entries
// all do.
const VERIFIED_NUTRITION_SOURCES = new Set([
  "usda", "fdc", "fdc_id",
  "off-verified", "off_upc",
  "admin", "admin-curated",
  "label-scan", "nutrition-label",
  "brand-nutrition",
]);

export const TIER_COLOR = {
  grey:  "#8a8a8a",
  green: "#7eb89b",
  blue:  "#5a8db8",
};

export const TIER_KICKER = {
  grey:  "In your kitchen only",
  green: "In the registry",
  blue:  "Verified",
};

export const TIER_BLURB = {
  grey:  "Just an entry — we don't know much about it yet.",
  green: "Recognized canonical. Nutrition may be an AI estimate.",
  blue:  "Verified data including calories, with a UPC or USDA tie.",
};

/**
 * canonicalTier(canonicalId, helpers)
 *   helpers: { findIngredient, getInfo, getPendingInfo }
 *
 * Returns: "grey" | "green" | "blue"
 */
export function canonicalTier(canonicalId, helpers = {}) {
  const { findIngredient, getInfo, getPendingInfo } = helpers;
  if (!canonicalId) return "grey";

  // Synthetic pending — not yet admin-approved into the global
  // registry. The owning user can use it; the rest of the world
  // can't see it. Stays grey until promotion.
  if (typeof getPendingInfo === "function") {
    const pending = getPendingInfo(canonicalId);
    if (pending) return "grey";
  }

  // Dangling reference — canonical id doesn't resolve to any registry
  // row (bundled or DB). Could be a deleted slug or a typo. Treat as
  // grey so the row is still placeable but the user knows the bind
  // is on shaky ground.
  const ing = typeof findIngredient === "function" ? findIngredient(canonicalId) : null;
  if (!ing) return "grey";

  const info = typeof getInfo === "function" ? getInfo(canonicalId) : null;

  // Nutrition extraction — info.nutrition wins (admin-promoted /
  // overlay), bundled ing.nutrition falls back. We accept either
  // kcal or calories as proof of "data present."
  const nutrition = info?.nutrition || ing?.nutrition || null;
  const hasNumbers = !!nutrition && (
    Number.isFinite(Number(nutrition.kcal)) ||
    Number.isFinite(Number(nutrition.calories))
  );

  // BLUE — verified-source nutrition. Source markers can live on the
  // nutrition object itself (`nutrition.source`) or one level up on
  // the info block (`info.nutrition_source` / `info.source`). We
  // check all three because different ingest paths stamp differently.
  const sourceMarker =
    nutrition?.source ||
    info?.nutrition_source ||
    (info?.source === "usda" || info?.source === "fdc" ? info.source : null);
  if (hasNumbers && sourceMarker && VERIFIED_NUTRITION_SOURCES.has(String(sourceMarker).toLowerCase())) {
    return "blue";
  }

  // GREY for bundled rows we know almost nothing about. A bundled
  // canonical with NO nutrition data anywhere (not bundled, not
  // overlay, not info) is functionally "just a name." User said:
  // "we don't have nutritional information on it and we don't know
  // if it's an accurate canonical." Same vibe.
  if (!hasNumbers && !info) return "grey";

  // GREEN — recognized canonical with at least SOME data, but the
  // nutrition either isn't tagged with a verified source or isn't
  // present at all (bundled rows often carry storage / units / cuts
  // without macros). Most of the registry sits here.
  return "green";
}
